import React, { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DealCard from "@/components/offers/DealCard";
import { ALL_CATEGORIES, ALL_MARKETPLACES, deals, Deal, Marketplace } from "@/constants/offers";

const FILTERS_KEY = "ofertas_filters_v1";

const PAGE_SIZE = 12;

const Ofertas: React.FC = () => {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("todas");
  const [marketplace, setMarketplace] = useState<string>("todos");
  const [page, setPage] = useState(1);

  // Load filters from sessionStorage
  useEffect(() => {
    const raw = sessionStorage.getItem(FILTERS_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        setSearch(parsed.search ?? "");
        setCategory(parsed.category ?? "todas");
        setMarketplace(parsed.marketplace ?? "todos");
      } catch {}
    }
  }, []);

  // Persist on change
  useEffect(() => {
    sessionStorage.setItem(
      FILTERS_KEY,
      JSON.stringify({ search, category, marketplace })
    );
  }, [search, category, marketplace]);

  const filtered = useMemo(() => {
    let items = deals as Deal[];
    if (marketplace !== "todos") {
      items = items.filter((d) => d.marketplace === marketplace);
    }
    if (category !== "todas") {
      items = items.filter((d) => d.category === category);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          d.tags?.some((t) => t.toLowerCase().includes(q))
      );
    }
    return items.sort((a, b) => +new Date(b.discoveredAt) - +new Date(a.discoveredAt));
  }, [search, category, marketplace]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    // reset to first page on filters change
    setPage(1);
  }, [search, category, marketplace]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <header className="bg-muted/40 border-b border-border">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <span className="inline-block text-brand-roxo font-semibold text-sm uppercase tracking-wider mb-3">
            Ofertas
          </span>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold text-brand-azul leading-tight">
            Compras em escala. <span className="text-brand-roxo">Preço em escala.</span>
          </h1>
          <p className="mt-3 text-brand-azul/70 max-w-2xl leading-relaxed">
            Seleção atualizada das melhores compras em marketplaces — com histórico de preço e link direto.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Badge className="bg-brand-roxo text-white hover:bg-brand-roxo/90">Atualizado</Badge>
            <Badge variant="outline" className="border-brand-azul/20 text-brand-azul">Amazon</Badge>
            <Badge variant="outline" className="border-brand-azul/20 text-brand-azul">Materiais, Ferramentas, EPI</Badge>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 pb-12">
        <Card className="mb-8 mt-8 border-border">
          <CardHeader>
            <CardTitle className="font-display text-brand-azul">Filtrar ofertas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input
                placeholder="Buscar por nome, marca, tag..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as categorias</SelectItem>
                  {ALL_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={marketplace} onValueChange={setMarketplace}>
                <SelectTrigger>
                  <SelectValue placeholder="Marketplace" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {ALL_MARKETPLACES.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => {
                    setSearch("");
                    setCategory("todas");
                    setMarketplace("todos");
                  }}
                >
                  Limpar filtros
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {paged.length === 0 ? (
          <div className="text-center text-brand-azul/70 py-16">
            Nenhuma oferta encontrada. Ajuste os filtros.
          </div>
        ) : (
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {paged.map((deal) => (
              <DealCard key={deal.id} deal={deal} />
            ))}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <div className="text-sm text-brand-azul/70">
            {filtered.length} ofertas encontradas
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <span className="text-sm text-brand-azul/80">
              Página {page} de {totalPages}
            </span>
            <Button
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Próxima
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Ofertas;
